import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Separator } from './ui/separator';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-effect-dark border-b border-orange-500/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="text-3xl font-bold text-white cursor-pointer hover:scale-105 transition-transform" 
              onClick={() => window.location.href = '/'}
            >
              <span className="text-orange-400 font-black text-3xl">Mega</span>videosee
            </div>
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="text-gray-300 border-gray-600 hover:text-orange-400 hover:border-orange-500"
            >
              ← Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-white flex items-center">
              <span className="mr-3 text-4xl">📋</span>
              Terms of Service
            </CardTitle>
            <p className="text-gray-400 text-lg">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </CardHeader>
          
          <CardContent className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using Megavideosee ("the Website", "our Service"), you accept and agree to be bound by 
                the terms and provision of this agreement. If you do not agree to abide by the above, please do not 
                use this service. These Terms of Service ("Terms") govern your access to and use of our website, 
                including any content, functionality, and services offered on or through the website.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Age Restrictions and Adult Content</h2>
              <div className="space-y-4">
                <p className="text-orange-400 font-medium">
                  ⚠️ IMPORTANT: This website contains explicit adult content intended solely for adults.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-400">
                  <li>You must be at least 18 years old to access this website</li>
                  <li>You confirm that you are of legal age in your jurisdiction to view adult content</li>
                  <li>You agree that you will not allow minors to access this website through your account</li>
                  <li>You understand that this website contains sexually explicit material</li>
                  <li>You are accessing this website for personal, non-commercial use only</li>
                </ul>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Permitted Use</h2>
              <div className="space-y-4">
                <p>You may use our website for lawful purposes only. You agree not to:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-400">
                  <li>Use the service for any unlawful purpose or to solicit unlawful acts</li>
                  <li>Violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                  <li>Infringe upon or violate our intellectual property rights or the rights of others</li>
                  <li>Harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                  <li>Submit false or misleading information</li>
                  <li>Upload or transmit viruses or any other type of malicious code</li>
                  <li>Collect or track personal information of others without consent</li>
                  <li>Spam, phish, pharm, pretext, spider, crawl, or scrape the website</li>
                  <li>Use the service for any obscene or immoral purpose</li>
                </ul>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Intellectual Property Rights</h2>
              <div className="space-y-4">
                <p>
                  The service and its original content, features, and functionality are and will remain the 
                  exclusive property of Megavideosee and its licensors. The service is protected by copyright, 
                  trademark, and other laws.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-400">
                  <li>All content on this website is protected by copyright and other intellectual property laws</li>
                  <li>You may not reproduce, distribute, modify, or create derivative works of our content</li>
                  <li>All trademarks, logos, and service marks are owned by their respective owners</li>
                  <li>User-generated content remains the property of the original creators</li>
                </ul>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Content and Conduct</h2>
              <div className="space-y-4">
                <h3 className="text-xl font-medium text-orange-400 mb-2">5.1 Content Standards</h3>
                <p>All content must comply with the following standards:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Must not contain illegal material or promote illegal activities</li>
                  <li>Must not violate intellectual property rights</li>
                  <li>Must not contain malicious software or harmful code</li>
                  <li>Must not be defamatory, obscene, offensive, or harassing</li>
                  <li>Must not violate privacy rights of any person</li>
                </ul>

                <h3 className="text-xl font-medium text-orange-400 mb-2 mt-6">5.2 Prohibited Content</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Content involving minors (under 18 years)</li>
                  <li>Non-consensual content or revenge porn</li>
                  <li>Content promoting violence or illegal activities</li>
                  <li>Copyrighted material without proper authorization</li>
                  <li>Content that violates applicable laws or regulations</li>
                </ul>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Privacy Policy</h2>
              <p>
                Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your 
                information when you use our service. By using our service, you agree to the collection and use of 
                information in accordance with our Privacy Policy.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Disclaimers and Limitation of Liability</h2>
              <div className="space-y-4">
                <h3 className="text-xl font-medium text-orange-400 mb-2">7.1 Service Disclaimer</h3>
                <p>
                  The information on this website is provided on an "as is" basis. To the fullest extent permitted by law, 
                  this Company excludes all representations, warranties, conditions and terms.
                </p>

                <h3 className="text-xl font-medium text-orange-400 mb-2">7.2 Limitation of Liability</h3>
                <p>
                  In no event shall Megavideosee, nor its directors, employees, partners, agents, suppliers, or affiliates, 
                  be liable for any indirect, incidental, special, consequential, or punitive damages, including without 
                  limitation, loss of profits, data, use, goodwill, or other intangible losses.
                </p>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. DMCA Notice</h2>
              <div className="space-y-4">
                <p>
                  We respect intellectual property rights and comply with the Digital Millennium Copyright Act (DMCA). 
                  If you believe your copyrighted work has been infringed, please contact us with:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Identification of the copyrighted work</li>
                  <li>Location of the infringing material on our website</li>
                  <li>Your contact information</li>
                  <li>A statement of good faith belief that use is unauthorized</li>
                  <li>A statement that the information provided is accurate</li>
                  <li>Your physical or electronic signature</li>
                </ul>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Termination</h2>
              <p>
                We may terminate or suspend your access immediately, without prior notice or liability, for any reason 
                whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use 
                the service will cease immediately.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Governing Law</h2>
              <p>
                These Terms shall be interpreted and governed by the laws of [Your Jurisdiction], without regard to its 
                conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be 
                considered a waiver of those rights.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Changes to Terms</h2>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision 
                is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Contact Information</h2>
              <div className="space-y-4">
                <p>
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-600">
                  <p className="text-orange-400 font-medium">Email:</p>
                  <p>legal@megavideosee.com</p>
                  
                  <p className="text-orange-400 font-medium mt-3">Address:</p>
                  <p>
                    Megavideosee Legal Department<br />
                    Terms of Service Inquiries<br />
                    [Your Business Address]
                  </p>
                </div>
              </div>
            </section>

            <div className="mt-12 text-center">
              <Button
                onClick={() => window.location.href = '/'}
                className="bg-orange-600 hover:bg-orange-700 px-8 py-3 text-lg"
              >
                Return to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TermsOfServicePage;